import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  castErrorResult,
  companyIdSelector,
  DrawerType,
  errors,
  IApiError,
  IRydeProcess,
  isTranslatableItem,
  ITranslatableItem,
  Log,
  normalizeErrors,
  NotificationsApi,
  PLURAL_KEY,
  RydeProcessTitle,
  SimpleResult,
  simpleResultErrorTechnicalError,
  useDrawers,
} from "@ryde/shared";

import { usePageContext } from "../../contexts";

export const useNotificationsBulkActions = () => {
  const { t } = useTranslation();
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setFetching = useSetRecoilState(apiDataFetchingAtom);

  const { entity } = usePageContext();
  const companyId = useRecoilValue(companyIdSelector);
  const { openDrawer } = useDrawers();

  const bulkUpdateMarkAsRead = useEvent(
    async (ids: number[]): Promise<SimpleResult<IRydeProcess>> => {
      try {
        setErrors(undefined);
        setFetching(true);

        Log.debug(
          `bulkUpdateMarkAsRead ${entity}, company id in selector ${companyId}`,
        );

        const result = await NotificationsApi.bulkUpdateMarkAsRead(ids);

        if (!result?.success) {
          if (!result?.error || !isTranslatableItem(result.error)) {
            setErrors([t(errors.common.technicalProblemOccurred)]);
            return simpleResultErrorTechnicalError(t);
          } else {
            setErrors([t((result?.error as ITranslatableItem).translationKey)]);
            return castErrorResult(result);
          }
        }

        if (result?.success) {
          const resultData = result.data as IRydeProcess;
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: resultData.id,
              processTitle: RydeProcessTitle.BulkUpdateNotificationAsRead,
              entity,
            },
          });
        }

        return result;
      } catch (error) {
        Log.errorAndToast(
          t("errors.bulkFailToUpdateEntities", {
            entity: t(`${entity}.${PLURAL_KEY}`),
          }),
          error,
        );
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        setErrors(errorsNormalized);
        return new SimpleResult({ error: errorsNormalized });
      } finally {
        setFetching(false);
      }
    },
  );

  const bulkUpdateResolved = useEvent(
    async (ids: number[]): Promise<SimpleResult<object>> => {
      try {
        setErrors(undefined);
        setFetching(true);
        Log.debug(
          `bulkUpdateResolved ${entity}, company id in selector ${companyId}`,
        );

        const result = await NotificationsApi.bulkUpdateResolved(ids);

        if (!result?.success) {
          if (!result?.error || !isTranslatableItem(result.error)) {
            setErrors([t(errors.common.technicalProblemOccurred)]);
            return simpleResultErrorTechnicalError(t);
          } else {
            setErrors([t((result?.error as ITranslatableItem).translationKey)]);
            return castErrorResult(result);
          }
        }

        if (result?.success) {
          const resultData = result.data as IRydeProcess;
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: resultData.id,
              processTitle: RydeProcessTitle.BulkUpdateNotificationResolved,
              entity,
            },
          });
        }

        return result;
      } catch (error) {
        Log.errorAndToast(
          t("errors.bulkFailToUpdateEntities", {
            entity: t(`${entity}.${PLURAL_KEY}`),
          }),
          error,
        );
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        setErrors(errorsNormalized);
        return new SimpleResult({ error: errorsNormalized });
      } finally {
        setFetching(false);
      }
    },
  );

  return {
    bulkUpdateMarkAsRead,
    bulkUpdateResolved,
  };
};
